import { toMercator } from "@turf/projection";
import type { Feature, LineString, MultiLineString, Position } from "geojson";
import type { Color } from "../color";

export type GradientLineFeature =
	| Feature<MultiLineString, { colors: Color[][] }>
	| Feature<LineString, { colors: Color[] }>;

type PositionWithColor = [number, number, number, number, number, number];

const MERCATOR_WORLD_WIDTH_METERS = toMercator([180, 0])[0] * 2;

// Use Turf's projection module because importing MapLibre's MercatorCoordinate
// pulls the entire MapLibre bundle into the worker.
// Normalize projected meters to a world width of 1, with the origin at the northwest.
function lngLatToNormalizedMercator(coordinate: Position) {
	const [x, y] = toMercator(coordinate);
	return {
		x: 0.5 + x / MERCATOR_WORLD_WIDTH_METERS,
		y: 0.5 - y / MERCATOR_WORLD_WIDTH_METERS,
	};
}

/**
 * To render a line the shader generates two triangles for each segment.
 * We send the current position, the previous position, and the next position.
 * The values passed in here are just the positions themselves.
 * The shader will offset the positions based on the thickness of the line using the side value
 * to determine which side to offset.
 */
function generateBufferData(paths: PositionWithColor[][]) {
	const verts: number[] = [];

	for (const line of paths) {
		for (let i = 0; i < line.length; i++) {
			const prev = i === 0 ? line[i] : line[i - 1];
			const next = i === line.length - 1 ? line[i] : line[i + 1];
			const pos = line[i];

			// One vertex for each side of the line segment
			for (const side of [-1, 1]) {
				verts.push(
					prev[0], // a_prev (lng)
					prev[1], // a_prev (lat)
					pos[0], // a_pos (lng)
					pos[1], // a_pos (lat)
					next[0], // a_next (lng)
					next[1], // a_next (lat)
					side, // a_side
					pos[2], // a_r
					pos[3], // a_g
					pos[4], // a_b
					pos[5], // a_a
				);
			}
		}
	}
	return new Float32Array(verts);
}

/**
 * We need to tell the GPU how to connect the vertices to form triangles.
 * Each segment of the line consists of two triangles. We reference the
 * vertices from the generateVertices function to create these triangles.
 */
function generateIndices(paths: PositionWithColor[][]) {
	const indices: number[] = [];
	let base = 0;
	for (const line of paths) {
		const n = line.length;
		for (let i = 0; i < n - 1; i++) {
			const i0 = base + i * 2; // Prev position left vertex
			const i1 = base + i * 2 + 1; // Prev position right vertex
			const i2 = base + i * 2 + 2; // Current position left vertex
			const i3 = base + i * 2 + 3; // Current position right vertex

			// Create two triangles for each segment
			// Triangle 1: prev left, current left, current right
			// Triangle 2: prev left, current right, prev right
			// curr left *---* curr right
			//           |  /|
			//           | / |
			//           |/  |
			// prev left *---* prev right
			indices.push(i0, i2, i3, i0, i3, i1);
		}
		base += n * 2;
	}
	return new Uint32Array(indices);
}

/**
 * Generates vertex data for a gradient line layer.
 * Each line segment is represented by an array of coordinates, and each coordinate
 * has an associated color.
 * We offset the coordinates by the origin to so that the coordinates are centered
 * around the first coordinate in the line. If we don't do this, at high zoom levels
 * a part of the projection into screen space will have very large values which can
 * cause floating point precision issues and the line does not render correctly.
 * It is jittery and the line is not smooth.
 * @param coordinates Array of line segments, each segment is an array of coordinates.
 * @param colorArray Array of colors for each segment, matching the coordinates. There
 * should be one color for each point in the segment.
 * @param origin The normalized Mercator origin to offset the coordinates by.
 * This is typically the first coordinate in the first segment.
 * @returns An array of vertex data for each segment, where each vertex is an array
 * of [x, y, r, g, b, a] values.
 */
export function generateVertexData(
	coordinates: Position[][],
	colorArray: Color[][],
	origin: { x: number; y: number },
): PositionWithColor[][] {
	return coordinates.map((line, segmentIndex) => {
		if (segmentIndex < 0 && segmentIndex > colorArray.length - 1) {
			console.error(
				`Color array does not contain a color for segment index ${segmentIndex}.`,
				"Ensure the color array matches the number of segments in the geojson.",
				"Expected color array length: ",
				coordinates.length,
				"Received color array length: ",
				colorArray.length,
			);
			return [];
		}

		return line.map((coord, pointIndex) => {
			const mercator = lngLatToNormalizedMercator(coord);
			if (
				pointIndex < 0 ||
				pointIndex >= colorArray[segmentIndex].length
			) {
				console.error(
					`Color array does not contain a color for point index ${pointIndex} in segment ${segmentIndex}.`,
					"Ensure the color array matches the number of points in the segment.",
					"Expected color array length: ",
					line.length,
					"Received color array length: ",
					colorArray[segmentIndex].length,
				);
				return [mercator.x, mercator.y, 0, 0, 0, 0];
			}
			return [
				mercator.x - origin.x,
				mercator.y - origin.y,
				...colorArray[segmentIndex][pointIndex],
			];
		});
	});
}

// Isolate geometry preparation so the worker can run it without the layer's Vue/GL lifecycle.
export function generateGradientLineBuffers(data: GradientLineFeature) {
	const coordinates =
		data.geometry.type === "LineString"
			? [data.geometry.coordinates]
			: data.geometry.coordinates;
	const colors =
		data.geometry.type === "LineString"
			? [data.properties.colors as Color[]]
			: (data.properties.colors as Color[][]);
	const originCoordinates: [number, number] = (coordinates[0]?.[0] as
		| [number, number]
		| undefined) ?? [0, 0];
	const origin = lngLatToNormalizedMercator(originCoordinates);
	const lines = generateVertexData(coordinates, colors, origin);
	return {
		origin: originCoordinates,
		vertices: generateBufferData(lines),
		indices: generateIndices(lines),
	};
}

export type GradientLineBuffers = ReturnType<
	typeof generateGradientLineBuffers
>;
